/**
 * User Apps store module (Phase 3 — My Apps dashboard).
 *
 * Holds the user's UTS instance list, a per-instance runtime cache
 * (detail / pods / snapshots / preflight / connection), and a light polling
 * loop that keeps the list's live status fresh while the dashboard is open.
 *
 * Scope (2026-06-02): the backend's `GET /uts/instances` default now returns
 * every instance the caller has `workload:viewer` permission on — across
 * ALL organizations — using the cs-packages v2.6 hierarchical permission
 * resolver. A user with a cross-org grant sees both orgs' workloads in
 * one list. We pass no `organization_id` here so the dashboard reflects
 * everything they're allowed to see. To restrict to the active org (e.g.
 * for the per-tier quota count on the Dashboard Apps card) the backend
 * exposes `?scope=mine`, but Dashboard.vue sources its numerator from
 * `/uts/quota/check` (already per-org) and doesn't need a narrow list.
 *
 * Namespaced and NOT persisted (live data, re-fetched on entry).
 */
import { utsApi, connectionApi, isolationApi } from '@/services/uts-api'
import { connectionTargetOf } from '@/utils/connection-target'
import { toRow } from '@/utils/instance-shape'

const POLL_MS = 15000

// Coalesce concurrent loadInstances calls. On a fresh My Apps mount, three
// dispatches fire in the same tick — startListPoll's immediate load, the
// component's mounted() load, and the org/filter watcher — each hitting the
// ~13s GET /uts/instances. While one fetch is in flight, later callers join
// the SAME request instead of firing their own. Keyed by org scope so an org
// switch still triggers a genuinely fresh fetch.
let _instancesInflight = null // { key: string, promise: Promise }

const state = () => ({
  instances: [], // raw instance objects (org-scoped)
  rows: [], // projected view-model rows (instance-shape.toRow)
  listLoading: false,
  listError: null,
  loaded: false,

  // The active org id used to scope the last list fetch (for display).
  scopeOrgId: null,

  // Suspended-trial signal from vclusters/isolation-status.
  isolation: null, // { provisioned, suspended, status, tier, namespace, vcluster_name }

  // Per-instance runtime cache, keyed by instance id.
  runtime: {}, // id -> { detail, pods, snapshots, preflight, connection, fetchedAt }

  // Polling handle for the list refresh (single global interval).
  listPollHandle: null
})

const getters = {
  /** True when the current trial is suspended (expired) — gates destructive UI. */
  isSuspended(state) {
    return Boolean(state.isolation && state.isolation.suspended === true)
  },
  /** Convenience tier read-through. */
  tier(state) {
    return state.isolation?.tier || null
  }
}

function resolveOrgId(rootState) {
  const u = rootState?.auth?.user
  if (!u) return null
  return (
    u.current_organization?.id ??
    u.default_organization?.id ??
    u.organization_id ??
    null
  )
}

const mutations = {
  SET_LIST_LOADING(state, v) {
    state.listLoading = v
  },
  SET_LIST_ERROR(state, v) {
    state.listError = v
  },
  SET_INSTANCES(state, instances) {
    const arr = Array.isArray(instances) ? instances : []
    state.instances = arr
    state.rows = arr.map(toRow)
    state.loaded = true
  },
  SET_SCOPE_ORG(state, id) {
    state.scopeOrgId = id ?? null
  },
  SET_ISOLATION(state, iso) {
    state.isolation = iso || null
  },
  REMOVE_INSTANCE(state, id) {
    state.instances = state.instances.filter((i) => (i._id || i.id) !== id)
    state.rows = state.rows.filter((r) => r.id !== id)
    const next = { ...state.runtime }
    delete next[id]
    state.runtime = next
  },
  SET_RUNTIME(state, { id, patch }) {
    const prev = state.runtime[id] || {}
    state.runtime = {
      ...state.runtime,
      [id]: { ...prev, ...patch, fetchedAt: Date.now() }
    }
  },
  SET_LIST_POLL(state, handle) {
    state.listPollHandle = handle
  }
}

const actions = {
  /**
   * Load the permission-scoped instance list. `silent` skips the loading
   * spinner (used by the polling loop so live refreshes don't flicker
   * skeletons).
   *
   * Scoped to the ACTIVE organization (2026-06-05): My Apps shows the apps
   * of the org the user currently has selected, matching the org switcher's
   * mental model. Switching org re-scopes the list (and, downstream, the
   * cluster/namespace filter dropdowns, which derive from the rows). When the
   * caller has no active org (personal user, `orgId == null`) we pass nothing
   * and the backend returns their visible instances.
   *
   * (Superseding the 2026-06-02 cross-org "show everything" behaviour, which
   * made the org switcher a no-op — selecting org B still showed org A's apps.)
   */
  async loadInstances({ commit, rootState, state }, { silent = false } = {}) {
    const orgId = resolveOrgId(rootState)
    const key = String(orgId ?? '')

    // Join an already-running fetch of the same scope (dedupe the mount storm).
    // A non-silent joiner still shows the spinner until the shared fetch lands.
    if (_instancesInflight && _instancesInflight.key === key) {
      if (!silent) commit('SET_LIST_LOADING', true)
      try {
        await _instancesInflight.promise
      } catch (_) {
        // The owning call logs + surfaces the error; joiners just wait it out.
      } finally {
        if (!silent) commit('SET_LIST_LOADING', false)
      }
      return state.rows
    }

    if (!silent) {
      commit('SET_LIST_LOADING', true)
      commit('SET_LIST_ERROR', null)
    }
    commit('SET_SCOPE_ORG', orgId)
    // Org-scoped fetch: the backend filters by owner.organization_id and
    // permission-gates the org (a member can't enumerate an org they lack
    // workload:viewer on). Personal users (no org) fall through to a broad
    // visible-instances fetch.
    const promise = utsApi.listInstances(
      orgId != null ? { organization_id: orgId } : {}
    )
    _instancesInflight = { key, promise }
    try {
      const instances = await promise
      commit('SET_INSTANCES', instances)
    } catch (error) {
      console.error('Failed to load instances:', error)
      if (!silent) {
        commit('SET_LIST_ERROR', error.message || 'Failed to load your apps')
      }
    } finally {
      // Only clear if we're still the owner (a later scope change may have
      // replaced the in-flight entry).
      if (_instancesInflight && _instancesInflight.promise === promise) {
        _instancesInflight = null
      }
      if (!silent) commit('SET_LIST_LOADING', false)
    }
    return state.rows
  },

  /** Probe the trial/isolation status for the suspended-trial banner. */
  async loadIsolation({ commit, rootState }) {
    const u = rootState?.auth?.user
    const userId = u?.user_id ?? u?.id ?? null
    const orgId = resolveOrgId(rootState)
    // isolation-status is permission-scoped on organization_id (cluster-api answers 400
    // missing_scope without it). A user with no organisation yet — registered but not through
    // onboarding, 2026-09-24 — has nothing to probe: no isolation, no banner, no request.
    if (orgId == null) {
      commit('SET_ISOLATION', null)
      return null
    }
    try {
      const iso = await isolationApi.getStatus({
        user_id: userId,
        organization_id: orgId
      })
      commit('SET_ISOLATION', iso)
      return iso
    } catch (error) {
      // Non-fatal: the banner just won't show.
      console.warn('isolation-status failed:', error?.message || error)
      commit('SET_ISOLATION', null)
      return null
    }
  },

  /** Full instance detail (My App detail Overview). */
  async loadInstance({ commit }, id) {
    const detail = await utsApi.getInstance(id)
    commit('SET_RUNTIME', { id, patch: { detail } })
    return detail
  },

  async loadPods({ commit }, id) {
    const pods = await utsApi.getPods(id)
    commit('SET_RUNTIME', { id, patch: { pods } })
    return pods
  },

  async loadPreflight({ commit }, id) {
    const preflight = await utsApi.getPreflight(id)
    commit('SET_RUNTIME', { id, patch: { preflight } })
    return preflight
  },

  async loadSnapshots({ commit }, id) {
    const snapshots = await utsApi.getSnapshots(id)
    commit('SET_RUNTIME', { id, patch: { snapshots } })
    return snapshots
  },

  /** Take a manual config snapshot, then refresh the snapshot list. */
  async takeSnapshot({ dispatch }, { id, label } = {}) {
    const snap = await utsApi.takeSnapshot(id, label)
    await dispatch('loadSnapshots', id)
    return snap
  },

  /** Restore (revert) to a snapshot, then refresh snapshots + instance. */
  async revertSnapshot({ dispatch }, { id, snapshotId } = {}) {
    const result = await utsApi.revertSnapshot(id, snapshotId)
    await dispatch('loadSnapshots', id)
    // Revert re-deploys, so the cached instance changed — refresh it. The
    // component reloads its own /full payload separately.
    try { await dispatch('loadInstance', id) } catch { /* best-effort */ }
    return result
  },

  /**
   * Connection-info is keyed by cluster + namespace from the instance target.
   *
   * Credential values are hidden server-side (cluster-api 2.25.45) — the
   * payload carries stubs and a `secret_masking` report. `{ id, reveal: true }`
   * re-reads with `?reveal=1`, which the server grants only to a
   * `secret:viewer` and audits; it throws on refusal and the cached masked
   * payload is left untouched for the view to keep showing.
   */
  async loadConnection({ commit, state }, arg) {
    const opts = arg && typeof arg === 'object' ? arg : { id: arg }
    const id = opts.id
    const inst = state.runtime[id]?.detail
    const target = connectionTargetOf(inst) || {}
    const connection = await connectionApi.getConnectionInfo({
      ...target, reveal: opts.reveal === true
    })
    commit('SET_RUNTIME', { id, patch: { connection } })
    return connection
  },

  // ── Live list polling ──────────────────────────────────────────────────
  startListPoll({ commit, state, dispatch }) {
    if (state.listPollHandle) return
    const handle = setInterval(() => {
      dispatch('loadInstances', { silent: true })
    }, POLL_MS)
    commit('SET_LIST_POLL', handle)
  },
  stopListPoll({ commit, state }) {
    if (state.listPollHandle) {
      clearInterval(state.listPollHandle)
      commit('SET_LIST_POLL', null)
    }
  },

  // ── Lifecycle actions ────────────────────────────────────────────────────
  async restart(_ctx, id) {
    return utsApi.restartPods(id)
  },
  async retry(_ctx, id) {
    return utsApi.retryPipeline(id)
  },
  async redeploy(_ctx, id) {
    return utsApi.redeploy(id)
  },

  /** Re-fetch a single instance's row + cached runtime (no mutation). */
  async refreshInstance({ commit, dispatch, state }, id) {
    const detail = await utsApi.getInstance(id)
    commit('SET_RUNTIME', { id, patch: { detail } })
    // Patch the matching list row too so the table reflects the new state.
    const idx = state.instances.findIndex((i) => (i._id || i.id) === id)
    if (idx !== -1) {
      const next = [...state.instances]
      next[idx] = detail
      commit('SET_INSTANCES', next)
    } else {
      // Not in the current list (deep-linked) — refresh the whole list.
      await dispatch('loadInstances', { silent: true })
    }
    return detail
  },

  async fetchDeletePreview(_ctx, { id, mode, branch, include_pvcs }) {
    return utsApi.deletePreview(id, { mode, branch, include_pvcs })
  },

  /**
   * Delete an app. `options` (catalog instances only) carries
   * `include_pvcs` / `delete_namespace_if_empty`. The instance is removed
   * from the list ONLY on full success — a 207 partial delete throws (the
   * backend keeps the record so the user can retry), so the row stays.
   */
  async deleteApp({ commit }, { id, confirmation, mode = 'full_application', branch, options }) {
    const payload = { confirmation, mode, branch }
    if (options && typeof options === 'object') payload.options = options
    const result = await utsApi.deleteInstance(id, payload)
    commit('REMOVE_INSTANCE', id)
    return result
  }
}

export default {
  namespaced: true,
  state,
  getters,
  mutations,
  actions
}
